import * as yup from 'yup';

import { RFC_REGEX } from '@onboarding/domain/value-objects';

/**
 * Builds the full onboarding wizard yup schema.
 *
 * @param t - i18next translation function for the `onboarding` namespace
 * @returns Composed yup schema covering every step of the wizard
 */
export const buildWizardSchema = (t: (key: string) => string) =>
  yup.object({
    restaurantType: yup.string().required(),
    tableService: yup.object({
      hasTableService: yup.string().required(),
      skipAreaConfig: yup.boolean().default(false),
      areas: yup.array().when(['hasTableService', 'skipAreaConfig'], {
        is: (hasService: string, skip: boolean) => hasService === 'true' && !skip,
        then: (s) =>
          s
            .of(
              yup.object({
                name: yup
                  .string()
                  .required(t('steps.tableService.validation.areaNameRequired'))
                  .test('unique-area-name', t('steps.tableService.validation.areaNameDuplicate'), function (value) {
                    if (!value) return true;
                    const from = ((this as unknown) as {
                      from?: Array<{ value?: { areas?: Array<{ name?: string }> } }>;
                    }).from;
                    const areas = from?.[1]?.value?.areas ?? [];
                    const normalized = value.trim().toLowerCase();
                    const matches = areas.filter((a) => (a?.name ?? '').trim().toLowerCase() === normalized);
                    return matches.length <= 1;
                  }),
                tableCount: yup
                  .string()
                  .required(t('steps.tableService.validation.tableCountRequired'))
                  .test(
                    'is-valid-table-count',
                    t('steps.tableService.validation.tableCountPositive'),
                    (val) => !!val && /^[1-9]\d?$/.test(val),
                  ),
                prefix: yup.string().required(t('steps.tableService.validation.prefixRequired')),
              }),
            )
            .min(1, t('steps.tableService.validation.atLeastOneArea'))
            .required(),
        otherwise: (s) => s.notRequired(),
      }),
    }),
    cashRegister: yup.string().required(),
    tip: yup.string().required(),
    tax: yup.string().required(),
    receipt: yup.object({
      businessName: yup.string().trim().required(t('steps.receipt.validation.businessNameRequired')),
      legalName: yup.string().when('brands', {
        is: (brands: Array<{ useRegisteredData?: string }>) => brands?.some((b) => b.useRegisteredData === 'true'),
        then: (s) =>
          s
            .trim()
            .required(t('steps.receipt.validation.legalNameRequired'))
            .min(3, t('steps.receipt.validation.legalNameMinLength')),
        otherwise: (s) => s.notRequired(),
      }),
      rfc: yup.string().when('brands', {
        is: (brands: Array<{ useRegisteredData?: string }>) => brands?.some((b) => b.useRegisteredData === 'true'),
        then: (s) =>
          s
            .trim()
            .required(t('steps.receipt.validation.rfcRequired'))
            .matches(RFC_REGEX, { message: t('steps.receipt.validation.rfcInvalid'), excludeEmptyString: true }),
        otherwise: (s) => s.notRequired(),
      }),
      fiscalAddress: yup.string().when('brands', {
        is: (brands: Array<{ useRegisteredData?: string }>) => brands?.some((b) => b.useRegisteredData === 'true'),
        then: (s) => s.trim().required(t('steps.receipt.validation.fiscalAddressRequired')),
        otherwise: (s) => s.notRequired(),
      }),
      brands: yup.array().of(
        yup.object({
          brandUuid: yup.string().required(),
          brandName: yup.string().required(),
          useRegisteredData: yup.string().required(),
          legalName: yup
            .string()
            .test('brand-legal-name', t('steps.receipt.validation.legalNameRequired'), function (value) {
              const { from } = (this as unknown) as { from: Array<{ value: { brands?: unknown[] } }> };
              const brands = from?.[1]?.value?.brands ?? [];
              const isSingleBrand = brands.length === 1;
              const useRegistered = this.parent?.useRegisteredData;

              if (isSingleBrand || useRegistered === 'false') {
                if (!value?.trim()) return false;
                if (value.trim().length < 3) {
                  return this.createError({ message: t('steps.receipt.validation.legalNameMinLength') });
                }
                return true;
              }
              return true;
            }),
          rfc: yup.string().test('brand-rfc', t('steps.receipt.validation.rfcRequired'), function (value) {
            const { from } = (this as unknown) as { from: Array<{ value: { brands?: unknown[] } }> };
            const brands = from?.[1]?.value?.brands ?? [];
            const isSingleBrand = brands.length === 1;
            const useRegistered = this.parent?.useRegisteredData;

            if (isSingleBrand || useRegistered === 'false') {
              if (!value?.trim()) return false;
              if (!RFC_REGEX.test(value.trim())) {
                return this.createError({ message: t('steps.receipt.validation.rfcInvalid') });
              }
              return true;
            }
            return true;
          }),
          fiscalAddress: yup
            .string()
            .test('brand-fiscal-address', t('steps.receipt.validation.fiscalAddressRequired'), function (value) {
              const { from } = (this as unknown) as { from: Array<{ value: { brands?: unknown[] } }> };
              const brands = from?.[1]?.value?.brands ?? [];
              const isSingleBrand = brands.length === 1;
              const useRegistered = this.parent?.useRegisteredData;

              if (isSingleBrand || useRegistered === 'false') {
                return !!value?.trim();
              }
              return true;
            }),
        }),
      ),
    }),
  });
